import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useDebouncedValue from '@alltrails/core/hooks/useDebouncedValue';
import type { AreaSearchResult } from '@alltrails/types/AlgoliaResults';
import type Context from '@alltrails/types/Context';
import type { LocaleUnits } from '@alltrails/types/LocaleUnits';
import { useGetParkTrailsQuery } from '../redux/exploreServiceApi';
import {
  ExploreServiceState,
  updateSearchIsLoading,
  updateSearchResults,
  updateSearchResultsPlace,
  updateSearchResultsSummary
} from '../redux/exploreServiceSlice';
import { PlaceLocation } from '../types/ExploreServiceLocation';
import useLocationExploreServiceQueryArgs from './useLocationExploreServiceQueryArgs';

export function useParkTrailResults(park?: AreaSearchResult) {
  const dispatch = useDispatch();
  const { skipQuery } = useSelector((state: { exploreService: ExploreServiceState }) => state.exploreService);
  const displayMetric = useSelector((state: { context: Context }) => state.context.displayMetric);

  const memoizedQueryArgs = useLocationExploreServiceQueryArgs(park);
  const queryArgsWithLocaleUnits = useMemo(
    () => ({ ...memoizedQueryArgs, localeUnits: (displayMetric ? 'metric' : 'imperial') as LocaleUnits }),
    [memoizedQueryArgs, displayMetric]
  );
  const debouncedQueryArgs = useDebouncedValue(queryArgsWithLocaleUnits, 100);

  const { data, isFetching, isLoading } = useGetParkTrailsQuery(debouncedQueryArgs, {
    skip:
      skipQuery ||
      !park ||
      !(debouncedQueryArgs.location as PlaceLocation)?.placeType ||
      ('bottomLeft' in debouncedQueryArgs.location &&
        (debouncedQueryArgs.location.bottomLeft === undefined ||
          debouncedQueryArgs.location.bottomRight === undefined ||
          debouncedQueryArgs.location.topLeft === undefined ||
          debouncedQueryArgs.location.topRight === undefined ||
          debouncedQueryArgs.location.mapRotation === undefined))
  });

  const searchIsLoading = useMemo(() => isFetching || isLoading, [isFetching, isLoading]);

  useEffect(() => {
    dispatch(updateSearchIsLoading(searchIsLoading));
  }, [dispatch, searchIsLoading]);

  useEffect(() => {
    if (data?.searchResults !== undefined) {
      dispatch(updateSearchResults(data.searchResults));
    }
  }, [dispatch, data?.searchResults]);

  useEffect(() => {
    if (data?.summary !== undefined) {
      dispatch(updateSearchResultsSummary(data.summary));
    }
  }, [dispatch, data?.summary]);

  useEffect(() => {
    if (data?.place !== undefined) {
      dispatch(updateSearchResultsPlace(data.place));
    }
  }, [dispatch, data?.place]);
}
